/**
 * MANTENIMIENTO → PARTES DE TRABAJO (FASE 8.2)
 *
 * El PT es la ORDEN de trabajo técnico; la intervención, la visita real.
 * Tres pantallas:
 *   · Partes de trabajo (gestión): listado con filtros, alta, ficha.
 *   · Ficha: datos, técnicos, activos, intervenciones, materiales (9.5),
 *     incidencias, evidencias e historial; acciones según permiso y estado.
 *   · Mis partes (técnico, móvil primero): tarjetas grandes, ordenadas por
 *     EN CURSO, PENDIENTE, hoy, próximos.
 *
 * La UI sólo decide qué botones ofrecer; la API (permiso + ámbito) y la
 * base (0059: máquina de estados, especialidad, RLS) son la barrera.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type ActivoResumen,
  type AvisoProgramacion,
  type EventoPt,
  type OpcionesActivos,
  type ParteDetalle,
  type ParteResumen,
  type AccionPt,
  type TecnicoElegible,
} from '../api.ts';
import { BarraAcciones, EmptyState, Error as ErrorCaja, fecha, fechaHora, FiltrosResponsive, Modal, Panel, SinResultados, useDatos, Vacio } from '../componentes/base.tsx';
import {
  EnlaceVolver,
  EstadoPtEtiqueta,
  EvidenciasMnt,
  MOTIVOS_PENDIENTE,
  MOTIVOS_REAPERTURA,
  PrioridadEtiqueta,
  programacion,
  RESULTADOS,
  TIPOS_PT,
  useVolverAqui,
  type Volver,
} from '../componentes/mantenimiento.tsx';
import { estadoPtMantenimiento } from '../estados.ts';
import { PieHistorial, useHistorial } from '../componentes/historial.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useToast } from '../componentes/toast.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { AvisoDrill, chipsDrill, DRILL_VACIO, TEXTO_INDICADOR_PT } from '../componentes/indicadores-mnt.tsx';
import { MaterialesParte } from './materiales-pt.tsx';

const MIGAS = [{ texto: 'Mantenimiento' }, { texto: 'Partes de trabajo', a: '/mantenimiento/partes' }];

function erroresDe(e: unknown): Record<string, string> {
  return e instanceof FalloApi ? (e.erroresDeCampo ?? {}) : {};
}

function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{v === null || v === '' ? '—' : v}</div>
    </div>
  );
}

function Avisos({ avisos }: { avisos: readonly AvisoProgramacion[] }): ReactNode {
  if (avisos.length === 0) return null;
  return (
    <div className="nota aviso" role="status">
      <strong>Revisa la programación:</strong>
      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{avisos.map((a, k) => <li key={k}>{a.mensaje}</li>)}</ul>
    </div>
  );
}

// ---------------------------------------------------------------- listado (gestión)

//  F12.4: además, los filtros del drill-down de Indicadores (`indicador`, su periodo, motivo, ubicación…) y `volver`.
const DEFAULTS = {
  q: '', estado: '', tipo: '', centro: '', prioridad: '', tecnico: '', desde: '', hasta: '', pagina: '1', activo: '', activoNombre: '', ...DRILL_VACIO,
} as const;
const POR_PAGINA = 50;

export function PaginaPartes(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const lista = useListaUrl(DEFAULTS, 'pagina');
  const [altaAbierta, setAltaAbierta] = useState(false);
  const v = (k: keyof typeof DEFAULTS) => lista.valor(k);
  const pagina = Number(v('pagina')) || 1;

  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  const partes = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA) });
    const map: Record<string, string> = {
      q: 'texto', estado: 'estado', tipo: 'tipo', centro: 'idCentro', prioridad: 'prioridad', tecnico: 'idOperario', desde: 'desde', hasta: 'hasta',
      activo: 'idActivo', indicador: 'indicador', pDesde: 'pDesde', pHasta: 'pHasta', motivoPendiente: 'motivoPendiente',
      idUbicacion: 'idUbicacion', idCliente: 'idCliente',
    };
    for (const [k, param] of Object.entries(map)) if (v(k as keyof typeof DEFAULTS) !== '') p.set(param, v(k as keyof typeof DEFAULTS).trim());
    return api.get<{ filas: ParteResumen[]; total: number }>(`/api/mantenimiento/partes?${p.toString()}`);
  }, [v('q'), v('estado'), v('tipo'), v('centro'), v('prioridad'), v('tecnico'), v('desde'), v('hasta'), pagina,
    v('activo'), v('indicador'), v('pDesde'), v('pHasta'), v('motivoPendiente'), v('idUbicacion'), v('idCliente')]);
  const tecnicos = useDatos(
    () => (v('centro') === '' || !puede('partes:gestionar')
      ? Promise.resolve({ filas: [] as TecnicoElegible[] })
      : api.get<{ filas: TecnicoElegible[] }>(`/api/mantenimiento/tecnicos?idCentro=${v('centro')}`)),
    [v('centro')],
  );
  const contador = (['estado', 'tipo', 'centro', 'prioridad', 'tecnico', 'desde', 'hasta'] as const).filter((k) => v(k) !== '').length;
  //  F12.4 — lo que trajo el indicador, a la vista y quitable (técnico y activo, si llegaron sin poder elegirse aquí).
  const chips = chipsDrill((k) => v(k), TEXTO_INDICADOR_PT);
  if (v('tecnico') !== '' && v('tecnicoNombre') !== '') chips.push({ clave: 't', texto: `Técnico: ${v('tecnicoNombre')}`, quitar: { tecnico: '', tecnicoNombre: '' } });
  if (v('activo') !== '') chips.push({ clave: 'a', texto: `Activo: ${v('activoNombre') || 'elegido'}`, quitar: { activo: '', activoNombre: '' } });

  return (
    <Shell
      migas={[{ texto: 'Mantenimiento' }, { texto: 'Partes de trabajo' }]}
      titulo="Partes de trabajo"
      acciones={puede('partes:gestionar') ? <button className="principal" onClick={() => setAltaAbierta(true)}>Nuevo parte</button> : undefined}
    >
      <div className="panel">
        <div className="panel-cuerpo">
          <AvisoDrill volver={v('volver')} chips={chips} quitar={(c) => lista.fijarVarios(c.quitar as Partial<Record<keyof typeof DEFAULTS, string>>)} />
          <FiltrosResponsive
            contador={contador}
            busqueda={<input type="search" aria-label="Buscar por código o título" placeholder="Buscar PT o título…" value={v('q')} onChange={(ev) => lista.fijar('q', ev.target.value)} />}
          >
            <div className="filtros-rejilla">
              <label className="campo"><span>Estado</span>
                <select value={v('estado')} onChange={(ev) => lista.fijar('estado', ev.target.value)}>
                  <option value="">{v('indicador') === '' ? 'Vivos (sin cerrados ni anulados)' : 'Los del indicador'}</option>
                  {['ABIERTO', 'PLANIFICADO', 'EN_CURSO', 'PENDIENTE', 'REABIERTO', 'FINALIZADO', 'CERRADO', 'ANULADO'].map((e) => <option key={e} value={e}>{estadoPtMantenimiento(e).label}</option>)}
                  <option value="TODOS">Todos</option>
                </select>
              </label>
              <label className="campo"><span>Tipo</span>
                <select value={v('tipo')} onChange={(ev) => lista.fijar('tipo', ev.target.value)}>
                  <option value="">Todos</option>
                  {Object.entries(TIPOS_PT).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                </select>
              </label>
              <label className="campo"><span>Centro</span>
                <select value={v('centro')} onChange={(ev) => lista.fijarVarios({ centro: ev.target.value, tecnico: '' })}>
                  <option value="">Todos</option>
                  {(opciones.datos?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
                </select>
              </label>
              <label className="campo"><span>Técnico</span>
                <select value={v('tecnico')} disabled={v('centro') === ''} onChange={(ev) => lista.fijar('tecnico', ev.target.value)}>
                  <option value="">{v('centro') === '' ? 'Elige antes un centro' : 'Todos'}</option>
                  {(tecnicos.datos?.filas ?? []).map((t) => <option key={t.idOperario} value={t.idOperario}>{t.nombre}</option>)}
                </select>
              </label>
              <label className="campo"><span>Prioridad</span>
                <select value={v('prioridad')} onChange={(ev) => lista.fijar('prioridad', ev.target.value)}>
                  <option value="">Todas</option>
                  {['BAJA', 'MEDIA', 'ALTA', 'CRITICA'].map((p) => <option key={p} value={p}>{p === 'CRITICA' ? 'Crítica' : p.charAt(0) + p.slice(1).toLowerCase()}</option>)}
                </select>
              </label>
              <label className="campo"><span>Desde</span><input type="date" value={v('desde')} onChange={(ev) => lista.fijar('desde', ev.target.value)} /></label>
              <label className="campo"><span>Hasta</span><input type="date" value={v('hasta')} onChange={(ev) => lista.fijar('hasta', ev.target.value)} /></label>
              {contador > 0 && <button type="button" onClick={() => lista.limpiar()}>Limpiar filtros</button>}
            </div>
          </FiltrosResponsive>
        </div>
      </div>

      <div className="panel">
        <Panel estado={partes}>
          {(d) => (d.filas.length === 0 ? (
            //  UX-1C: vacío real ≠ vacío por filtros.
            lista.esPorDefecto
              ? <EmptyState titulo="No hay partes de trabajo abiertos." descripcion="Los partes nuevos, planificados, en curso o pendientes aparecerán aquí." />
              : <SinResultados />
          ) : (
            <>
              <div className="tabla-ots-desktop tabla-desliza">
                <table>
                  <thead><tr><th>Código</th><th className="col-opcional">Tipo</th><th>Prioridad</th><th>Centro</th><th>Técnicos</th><th>Programado</th><th>Estado</th><th className="col-opcional">Activos</th></tr></thead>
                  <tbody>
                    {d.filas.map((p) => (
                      <tr key={p.id}>
                        {/* UX-1D: el código en mono; el título, en la fuente de lectura. */}
                        <td><Link className="mono" to={`/mantenimiento/partes/${p.id}`}>{p.codigo}</Link><div className="pequena celda-subtitulo">{p.titulo}</div></td>
                        <td className="col-opcional">{TIPOS_PT[p.tipo]}</td>
                        <td><PrioridadEtiqueta prioridad={p.prioridad} /></td>
                        <td>{p.centro.nombre}</td>
                        <td>{p.tecnicos.map((t) => t.nombre).join(', ') || '—'}</td>
                        <td>{programacion(p.fechaProgramada, p.horaProgramada)}</td>
                        <td><EstadoPtEtiqueta estado={p.estado} />{p.motivoPendiente !== null && <div className="pequena">{MOTIVOS_PENDIENTE[p.motivoPendiente]}</div>}</td>
                        <td className="col-opcional">{p.activos.map((a) => a.nombre).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="lista-ots-movil" aria-label="Partes de trabajo">
                {d.filas.map((p) => <li key={p.id}><TarjetaParte parte={p} /></li>)}
              </ul>
              {d.total > POR_PAGINA && (
                <div className="panel-cuerpo fila separa">
                  <button disabled={pagina <= 1} onClick={() => lista.fijar('pagina', String(pagina - 1))}>Anterior</button>
                  <span className="suave">Página {pagina} de {Math.ceil(d.total / POR_PAGINA)}</span>
                  <button disabled={pagina * POR_PAGINA >= d.total} onClick={() => lista.fijar('pagina', String(pagina + 1))}>Siguiente</button>
                </div>
              )}
            </>
          ))}
        </Panel>
      </div>

      {altaAbierta && opciones.datos !== null && (
        <FormAltaParte opciones={opciones.datos} cerrar={() => setAltaAbierta(false)} alCrear={(id) => navegar(`/mantenimiento/partes/${id}`)} />
      )}
    </Shell>
  );
}

/** F12.1 — dónde: centro y, si la tiene, la ubicación de sus activos. Nunca «Dirección: —». */
export function textoDonde(p: Pick<ParteResumen, 'centro' | 'ubicacion'>): string {
  return [p.centro.nombre, p.ubicacion].filter((x) => x != null && x !== '').join(' · ');
}

/**
 * Tarjeta grande (móvil / Mis partes): se toca entera. F12.1 — lo que el técnico necesita para decidir:
 * cuándo, qué, dónde (centro · ubicación), con qué activo, prioridad y, si toca, por qué está pendiente.
 */
export function TarjetaParte({ parte: p, volver, a }: { parte: ParteResumen; volver?: { volver: Volver }; a?: string }): ReactNode {
  //  F14.3 — desde «Mis partes», a la vista de campo (con y sin conexión); el resto, a la ficha.
  return (
    <Link to={a ?? `/mantenimiento/partes/${p.id}`} state={volver} className="tarjeta-ot tarjeta-parte" aria-label={`${p.titulo} · ${textoDonde(p)}`}>
      <div className="tarjeta-ot-cabecera">
        <span className="tarjeta-parte-cuando">{p.fechaProgramada === null ? 'Sin fecha' : programacion(p.fechaProgramada, p.horaProgramada)}</span>
        <EstadoPtEtiqueta estado={p.estado} />
      </div>
      <div className="tarjeta-ot-datos">
        <span className="tarjeta-ot-fecha">{p.titulo}</span>
        <span className="tarjeta-ot-secundario">{textoDonde(p)}</span>
        {p.activos.length > 0 && <span className="tarjeta-ot-secundario">{p.activos.map((a) => a.nombre).join(' · ')}</span>}
        <span className="fila" style={{ flexWrap: 'wrap', gap: 6 }}>
          <PrioridadEtiqueta prioridad={p.prioridad} />
          {p.atrasado === true && <span className="etiqueta error">Atrasado</span>}
          {p.motivoPendiente !== null && <span className="etiqueta aviso">Pendiente: {MOTIVOS_PENDIENTE[p.motivoPendiente]}</span>}
          <span className="etiqueta neutra">{TIPOS_PT[p.tipo]}</span>
        </span>
        <span className="pequena tenue">{p.codigo}</span>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------- mis partes (técnico)

//  F14.3 — «Mis partes» vive en `mis-partes-campo.tsx`: la misma pantalla con y sin conexión.

// ---------------------------------------------------------------- alta

export function FormAltaParte({
  opciones,
  cerrar,
  alCrear,
  inicial = {},
}: {
  opciones: OpcionesActivos;
  cerrar: () => void;
  alCrear: (id: string) => void;
  inicial?: { idCentro?: string; idActivo?: string };
}): ReactNode {
  const [idCentro, setIdCentro] = useState(inicial.idCentro ?? (opciones.centros.length === 1 ? opciones.centros[0]!.id : ''));
  const [tipo, setTipo] = useState('CORRECTIVO');
  const [prioridad, setPrioridad] = useState('MEDIA');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fechaProg, setFechaProg] = useState('');
  const [horaProg, setHoraProg] = useState('');
  const [duracion, setDuracion] = useState('');
  const [idsActivos, setIdsActivos] = useState<string[]>(inicial.idActivo !== undefined ? [inicial.idActivo] : []);
  const [idsTecnicos, setIdsTecnicos] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);

  const activos = useDatos(
    () => (idCentro === '' ? Promise.resolve({ filas: [] as ActivoResumen[], total: 0 }) : api.get<{ filas: ActivoResumen[]; total: number }>(`/api/activos?idCentro=${idCentro}&porPagina=200`)),
    [idCentro],
  );
  const tecnicos = useDatos(
    () => (idCentro === '' ? Promise.resolve({ filas: [] as TecnicoElegible[] }) : api.get<{ filas: TecnicoElegible[] }>(`/api/mantenimiento/tecnicos?idCentro=${idCentro}`)),
    [idCentro],
  );
  const alternar = (lista: string[], id: string) => (lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);
  //  F12.2: llegando desde un activo, centro y activo ya se saben: se muestran, no se vuelven a preguntar.
  const desdeActivo = inicial.idActivo !== undefined && inicial.idCentro !== undefined;
  const elActivo = desdeActivo ? (activos.datos?.filas ?? []).find((a) => a.id === inicial.idActivo) : undefined;
  const elCentro = opciones.centros.find((c) => c.id === idCentro);

  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post<{ parte: ParteDetalle }>('/api/mantenimiento/partes', {
        idCentro, tipo, prioridad, titulo, descripcion: descripcion.trim() || null,
        ...(fechaProg !== '' || horaProg !== '' ? { fechaProgramada: fechaProg, horaProgramada: horaProg } : {}),
        duracionEstimadaMin: duracion === '' ? null : Number(duracion),
        idsActivos, idsTecnicos,
      });
      alCrear(r.parte.id);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Nuevo parte de trabajo" cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Nuevo parte de trabajo">
        {desdeActivo ? (
          <dl className="contexto-alta">
            <div><dt>Centro</dt><dd>{elCentro === undefined ? '…' : `${elCentro.cliente.nombre} — ${elCentro.nombre}`}</dd></div>
            <div><dt>Activo</dt><dd>{elActivo?.nombre ?? '…'}{elActivo?.titularidad === 'EMPRESA' ? ' (empresa prestadora)' : ''}</dd></div>
            {elActivo?.ubicacion != null && <div><dt>Ubicación</dt><dd>{elActivo.ubicacion.nombre}</dd></div>}
          </dl>
        ) : (
        <div className="campo">
          <label htmlFor="pt-centro">Centro</label>
          <select id="pt-centro" value={idCentro} required onChange={(ev) => { setIdCentro(ev.target.value); setIdsActivos([]); setIdsTecnicos([]); }}>
            <option value="">Elige un centro…</option>
            {opciones.centros.map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
          </select>
          {campos['idCentro'] !== undefined && <div className="error-campo">{campos['idCentro']}</div>}
        </div>
        )}
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo">
            <label htmlFor="pt-tipo">Tipo</label>
            <select id="pt-tipo" value={tipo} onChange={(ev) => setTipo(ev.target.value)}>
              {Object.entries(TIPOS_PT).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="pt-prioridad">Prioridad</label>
            <select id="pt-prioridad" value={prioridad} onChange={(ev) => setPrioridad(ev.target.value)}>
              {['BAJA', 'MEDIA', 'ALTA', 'CRITICA'].map((p) => <option key={p} value={p}>{p === 'CRITICA' ? 'Crítica' : p.charAt(0) + p.slice(1).toLowerCase()}</option>)}
            </select>
          </div>
        </div>
        <div className="campo">
          <label htmlFor="pt-titulo">Título</label>
          <input id="pt-titulo" value={titulo} required placeholder={elActivo === undefined ? undefined : `Revisar ${elActivo.nombre}`} onChange={(ev) => setTitulo(ev.target.value)} />
          {campos['titulo'] !== undefined && <div className="error-campo">{campos['titulo']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="pt-descripcion">Descripción</label>
          <textarea id="pt-descripcion" rows={3} value={descripcion} onChange={(ev) => setDescripcion(ev.target.value)} />
        </div>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="pt-fecha">Fecha programada</label><input id="pt-fecha" type="date" value={fechaProg} onChange={(ev) => setFechaProg(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="pt-hora">Hora</label><input id="pt-hora" type="time" value={horaProg} onChange={(ev) => setHoraProg(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="pt-duracion">Duración (min)</label><input id="pt-duracion" type="number" min={1} value={duracion} onChange={(ev) => setDuracion(ev.target.value)} /></div>
        </div>
        {(campos['fechaProgramada'] ?? campos['horaProgramada']) !== undefined && <div className="error-campo">{campos['fechaProgramada'] ?? campos['horaProgramada']}</div>}
        {idCentro !== '' && (
          <>
            {!desdeActivo && <fieldset className="campo">
              <legend>Activos (opcional)</legend>
              {(activos.datos?.filas ?? []).length === 0 ? <p className="suave pequena">Este centro no tiene activos en uso.</p>
                : (activos.datos?.filas ?? []).map((a) => (
                  <label key={a.id} className="fila"><input type="checkbox" checked={idsActivos.includes(a.id)} onChange={() => setIdsActivos((l) => alternar(l, a.id))} /> {a.nombre}{a.titularidad === 'EMPRESA' ? ' · empresa prestadora' : ''} <span className="suave pequena">{a.codigo}</span></label>
                ))}
            </fieldset>}
            <fieldset className="campo">
              <legend>Técnicos (opcional)</legend>
              {(tecnicos.datos?.filas ?? []).length === 0 ? <p className="suave pequena">No hay técnicos de mantenimiento habilitados en este centro.</p>
                : (tecnicos.datos?.filas ?? []).map((t) => (
                  <label key={t.idOperario} className="fila"><input type="checkbox" checked={idsTecnicos.includes(t.idOperario)} onChange={() => setIdsTecnicos((l) => alternar(l, t.idOperario))} /> {t.nombre}</label>
                ))}
            </fieldset>
          </>
        )}
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila">
          <button type="submit" className="principal" disabled={enviando}>{enviando ? 'Creando…' : 'Crear parte'}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

/** `/mantenimiento/partes/nuevo?idCentro=&idActivo=` — llegada desde la ficha (QR) de un activo. */
export function PaginaNuevoParte(): ReactNode {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const desde = useLocation().state as { volver?: Volver } | null;
  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  const idActivo = params.get('idActivo') ?? undefined;
  return (
    <Shell migas={[...MIGAS, { texto: 'Nuevo' }]} titulo="Nuevo parte de trabajo">
      <Panel estado={opciones}>
        {(o) => (
          <FormAltaParte
            opciones={o}
            inicial={{ idCentro: params.get('idCentro') ?? undefined, ...(idActivo !== undefined ? { idActivo } : {}) }}
            cerrar={() => navegar(-1)}
            alCrear={(id) => navegar(`/mantenimiento/partes/${id}`, { replace: true, state: desde })}
          />
        )}
      </Panel>
    </Shell>
  );
}

// ---------------------------------------------------------------- ficha

type Dialogo = 'editar' | 'planificar' | 'pendiente' | 'finalizar-parte' | 'cerrar' | 'anular' | 'finalizar-intervencion' | 'reabrir' | 'resolver-reapertura'
  | 'nueva-intervencion' | null;

/**
 * UX-1B — acciones del parte: UNA principal, como mucho UNA secundaria y el resto en «Más acciones» (Anular,
 * la única destructiva, siempre ahí). F12.1 — QUÉ acciones hay lo decide el servidor (`p.acciones`, mismas
 * reglas que luego exige); aquí sólo se nombran y se colocan. El orden del servidor es la prioridad.
 */
function AccionesParte({ p, pendientes, abrir, iniciarPropia, finalizarPropia }: {
  p: ParteDetalle; pendientes: number;
  abrir: (d: Exclude<Dialogo, null>) => void; iniciarPropia: () => void; finalizarPropia: () => void;
}): ReactNode {
  type Candidata = { texto: string; alPulsar: () => void; destacable: boolean; deshabilitada?: boolean; describe?: string };
  const acciones = p.acciones ?? [];
  const c: Candidata[] = [];
  const CANDIDATAS: Partial<Record<AccionPt, Candidata>> = {
    FINALIZAR_INTERVENCION: { texto: 'Finalizar intervención', alPulsar: finalizarPropia, destacable: true },
    CERRAR: { texto: 'Cerrar parte', alPulsar: () => abrir('cerrar'), destacable: true, deshabilitada: pendientes > 0, describe: pendientes > 0 ? 'cierre-bloqueado' : undefined },
    FINALIZAR: { texto: 'Finalizar parte', alPulsar: () => abrir('finalizar-parte'), destacable: true },
    INICIAR: { texto: 'Iniciar intervención', alPulsar: iniciarPropia, destacable: true },
    //  F12.1: misma operación que iniciar (una intervención nueva), con el nombre de lo que es para el técnico.
    RETOMAR: { texto: 'Retomar trabajo', alPulsar: iniciarPropia, destacable: true },
    NUEVA_INTERVENCION: { texto: 'Nueva intervención', alPulsar: () => abrir('nueva-intervencion'), destacable: true },
    PLANIFICAR: { texto: 'Planificar', alPulsar: () => abrir('planificar'), destacable: true },
    RESOLVER_REAPERTURA: { texto: 'Finalizar sin nueva intervención', alPulsar: () => abrir('resolver-reapertura'), destacable: false },
    PONER_PENDIENTE: { texto: 'Poner pendiente', alPulsar: () => abrir('pendiente'), destacable: false },
    //  Reabrir es excepcional: aunque sea lo único posible en un CERRADO, no se pinta como principal.
    REABRIR: { texto: 'Reabrir parte', alPulsar: () => abrir('reabrir'), destacable: false },
  };
  for (const a of acciones) { const x = CANDIDATAS[a]; if (x !== undefined) c.push(x); }
  const editar = acciones.includes('EDITAR');
  const anular = acciones.includes('ANULAR');
  if (c.length === 0 && !editar && !anular) return null;

  const boton = (x: Candidata, principal: boolean): ReactNode => (
    <button type="button" className={principal ? 'principal grande' : 'grande'} disabled={x.deshabilitada === true}
      aria-describedby={x.describe} onClick={x.alPulsar}>{x.texto}</button>
  );
  const [primera, segunda, ...resto] = c;
  return (
    <BarraAcciones etiqueta="Acciones del parte" className="acciones-parte"
      principal={primera === undefined ? undefined : boton(primera, primera.destacable)}
      secundaria={segunda === undefined ? undefined : boton(segunda, false)}
      mas={[
        ...resto.map((x) => ({ texto: x.texto, alPulsar: x.alPulsar, deshabilitada: x.deshabilitada })),
        editar && { texto: 'Editar', alPulsar: () => abrir('editar') },
        anular && { texto: 'Anular', alPulsar: () => abrir('anular'), peligro: true },
      ]} />
  );
}

/** FASE 9.5-B — «1 consumo pendiente de resolver y 1 devolución pendiente de validación» (nunca «consumo» por una devolución). */
export function textoPendientes(m: { consumos: number; devoluciones: number }): string {
  const partes: string[] = [];
  if (m.consumos > 0) partes.push(m.consumos === 1 ? '1 consumo pendiente de resolver' : `${String(m.consumos)} consumos pendientes de resolver`);
  if (m.devoluciones > 0) partes.push(m.devoluciones === 1 ? '1 devolución pendiente de validación' : `${String(m.devoluciones)} devoluciones pendientes de validación`);
  return partes.join(' y ');
}

/** Error de la API al cerrar/anular con material pendiente, con enlace a la sección Materiales. */
function AvisoPendientes({ verbo, m }: { verbo: 'cerrar' | 'anular'; m: { consumos: number; devoluciones: number } }): ReactNode {
  return (
    <div className="nota error" role="alert">
      <strong>No se puede {verbo} el parte. Quedan elementos pendientes de resolver.</strong>{' '}
      Hay {textoPendientes(m)}.{' '}
      <a href="#materiales" onClick={() => document.getElementById('materiales')?.scrollIntoView?.({ block: 'start' })}>Ver materiales pendientes</a>
    </div>
  );
}

export function PaginaParte(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const parte = useDatos(() => api.get<ParteDetalle>(`/api/mantenimiento/partes/${encodeURIComponent(id)}`), [id]);
  //  FASE 9.5-D (UAT): al volver a la pestaña, la ficha se relee — otro usuario puede haber resuelto el material
  //  (y «Cerrar parte» seguiría deshabilitado con datos viejos).
  const recargarParte = parte.recargar;
  useEffect(() => {
    const alVolver = (): void => { if (document.visibilityState !== 'hidden') recargarParte(); };
    window.addEventListener('focus', alVolver);
    return () => window.removeEventListener('focus', alVolver);
  }, [recargarParte]);
  const historial = useHistorial<EventoPt>(`/api/mantenimiento/partes/${encodeURIComponent(id)}/historial`);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  /** Intervención que se finaliza: la propia o (quien gestiona) la abierta de un técnico. */
  const [intervencion, setIntervencion] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<AvisoProgramacion[]>([]);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();

  function recargar(nuevosAvisos: AvisoProgramacion[] = []): void {
    setDialogo(null);
    setAvisos(nuevosAvisos);
    parte.recargar();
    historial.recargar();
  }

  /** UX-1C: `exito` → toast ligero al terminar bien. Los errores siguen junto a la página (contexto y enlaces). */
  async function accion(f: () => Promise<unknown>, exito?: string): Promise<void> {
    setError(null);
    try {
      const r = await f();
      const av = (r as { avisos?: AvisoProgramacion[] } | undefined)?.avisos ?? [];
      recargar(av);
      if (exito !== undefined) toast.exito(exito);
    } catch (e) {
      //  FASE 9.5/9.5-B: si el cierre/anulación choca con material pendiente, el diálogo se cierra para que se
      //  vea el aviso y su enlace a los materiales (hallazgo UAT: el modal tapaba el mensaje); y la ficha se
      //  recarga para que «Cerrar parte» refleje lo pendiente.
      if (e instanceof FalloApi && e.codigo === 'PT_PENDIENTES_RESOLVER') { setDialogo(null); parte.recargar(); }
      setError(e);
    }
  }

  if (parte.error instanceof FalloApi && (parte.error.estado === 404 || parte.error.estado === 403)) {
    return (
      <Shell migas={MIGAS} titulo="Parte no disponible">
        <div className="panel"><Vacio titulo="Este parte no existe o no tienes acceso"><Link to="/mantenimiento/partes">Volver</Link></Vacio></div>
      </Shell>
    );
  }
  const p = parte.datos;
  const gestiona = puede('partes:gestionar');
  const volverAqui = useVolverAqui(`el parte ${parte.datos?.codigo ?? ''}`.trim());
  const vivo = p !== null && !['CERRADO', 'ANULADO'].includes(p.estado);
  //  Tolerante con una API anterior a 9.5-B (sin el campo): la base sigue exigiéndolo al cerrar.
  const material = p?.materialPendiente ?? { consumos: 0, devoluciones: 0 };
  const pendientes = material.consumos + material.devoluciones;
  const base = `/api/mantenimiento/partes/${id}`;

  return (
    <Shell
      migas={[...MIGAS, { texto: p?.codigo ?? '…' }]}
      titulo={p === null ? 'Parte de trabajo' : p.titulo}
      estado={p === null ? undefined : <EstadoPtEtiqueta estado={p.estado} />}
      meta={p === null ? undefined : (
        //  F12.1: dónde ir, lo primero (centro · ubicación · activo); el código, al final y discreto.
        <><span className="donde-pt">{textoDonde(p)}{p.activos.length > 0 && ` · ${p.activos.map((a) => a.nombre).join(', ')}`}</span>
          <PrioridadEtiqueta prioridad={p.prioridad} /><span className="etiqueta neutra">{TIPOS_PT[p.tipo]}</span>
          {p.solicitadoPorCliente === true && <span className="etiqueta info">Solicitado por el cliente</span>}
          <span className="pequena tenue">{p.codigo}</span></>
      )}
    >
     <div className="ficha-pt">
      <EnlaceVolver />
      {p !== null && (
        <AccionesParte p={p} pendientes={pendientes}
          abrir={(d) => setDialogo(d)}
          iniciarPropia={() => void accion(() => api.post(`${base}/intervenciones`, {}), p.estado === 'PENDIENTE' || p.estado === 'REABIERTO' ? 'Trabajo retomado.' : 'Intervención iniciada.')}
          finalizarPropia={() => { setIntervencion(p.miIntervencionAbierta); setDialogo('finalizar-intervencion'); }} />
      )}
      {/* FASE 9.5-D: FINALIZADO ≠ CERRADO. Visible para cualquiera que lea la ficha, no sólo para quien puede cerrar. */}
      {p !== null && p.estado === 'FINALIZADO' && pendientes > 0 && !(error instanceof FalloApi && error.codigo === 'PT_PENDIENTES_RESOLVER') && (
        <p id="cierre-bloqueado" className="nota aviso" role="status">
          <strong>Trabajo finalizado. {pendientes === 1 ? 'Queda 1 elemento de material pendiente' : `Quedan ${String(pendientes)} elementos de material pendientes`} de resolver antes de poder cerrar el parte.</strong>{' '}
          Hay {textoPendientes(material)}.{' '}
          <a href="#materiales" onClick={() => document.getElementById('materiales')?.scrollIntoView?.({ block: 'start' })}>Ver materiales pendientes</a>
        </p>
      )}
      {error instanceof FalloApi && error.codigo === 'PT_PENDIENTES_RESOLVER' ? (
        <AvisoPendientes verbo={error.message.startsWith('No se puede anular') ? 'anular' : 'cerrar'}
          m={{ consumos: Number((error.detalles as { consumosPendientes?: number } | undefined)?.consumosPendientes ?? 0),
               devoluciones: Number((error.detalles as { devolucionesPendientes?: number } | undefined)?.devolucionesPendientes ?? 0) }} />
      ) : error !== null && <ErrorCaja error={error} />}
      <Avisos avisos={avisos} />

      <Panel estado={parte}>
        {(d) => (
          <>
            {d.estado === 'PENDIENTE' && d.motivoPendiente !== null && (
              <div className="nota aviso" role="status"><strong>Pendiente: {MOTIVOS_PENDIENTE[d.motivoPendiente]}.</strong> {d.detallePendiente}</div>
            )}
            {d.estado === 'ANULADO' && <div className="nota error" role="status"><strong>Anulado.</strong> {d.motivoAnulacion}</div>}
            {d.estado === 'CERRADO' && <div className="nota exito" role="status"><strong>Cerrado</strong> por {d.cerradoPor?.nombre} · {fechaHora(d.cerradoEn)}. {d.observacionesCierre}</div>}
            {d.estado === 'REABIERTO' && d.reapertura != null && (
              <div className="nota aviso" role="status">
                <strong>Reabierto</strong>{d.reapertura.por !== null && <> por {d.reapertura.por.nombre}</>}{d.reapertura.en !== null && <> · {fechaHora(d.reapertura.en)}</>}.{' '}
                Motivo: {MOTIVOS_REAPERTURA[d.reapertura.motivo] ?? d.reapertura.motivo} — {d.reapertura.detalle}{' '}
                <span className="suave">El cierre anterior está en el historial.</span>
              </div>
            )}
            {/* F12.2: terminado todo el trabajo de una incidencia, se ofrece resolverla (no se resuelve sola). */}
            {puede('incidencias_mnt:gestionar') && d.incidencias.filter((i) => i.porResolver === true).map((i) => (
              <div key={i.id} className="nota exito" role="status">
                <strong>Todos los trabajos relacionados están terminados.</strong> ¿Resolver también la incidencia «{i.titulo}»?{' '}
                <Link className="boton principal" to={`/mantenimiento/incidencias/${i.id}?resolver=1`} state={volverAqui}>Resolver la incidencia</Link>
              </div>
            ))}
            {['ABIERTO', 'PLANIFICADO', 'EN_CURSO', 'PENDIENTE', 'REABIERTO'].includes(d.estado) && d.incidencias.filter((i) => i.estado === 'RESUELTA').map((i) => (
              <div key={i.id} className="nota aviso" role="status">
                La incidencia «{i.titulo}» ya figura como resuelta, pero este parte vuelve a tener trabajo.
              </div>
            ))}
            {/* F12.1: primero lo que hay que hacer y dónde; la información administrativa, al final. */}
            <section className="panel" aria-label="Qué hay que hacer">
              <div className="panel-cabecera"><h2>Qué hay que hacer</h2></div>
              {d.descripcion !== null && <div className="panel-cuerpo"><p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{d.descripcion}</p></div>}
              <div className="panel-cuerpo datos">
                <Dato e="Dónde" v={textoDonde(d)} />
                {d.activosDetalle.length > 0 && (
                  <Dato e={d.activosDetalle.length === 1 ? 'Activo' : 'Activos'} v={
                    <ul className="lista-sin-estilo">
                      {d.activosDetalle.map((a) => (
                        <li key={a.id}><Link to={`/activos/${a.id}`} state={volverAqui}>{a.nombre}</Link> <span className="suave pequena">{a.tipo}</span>
                          {a.estado !== 'OPERATIVO' && <> <span className={`etiqueta ${a.estado === 'BAJA' ? 'oscura' : 'aviso'}`}>{a.estado === 'BAJA' ? 'Baja' : 'Fuera de servicio'}</span></>}</li>
                      ))}
                    </ul>
                  } />
                )}
                <Dato e="Cuándo" v={d.fechaProgramada === null ? 'Sin programar' : <>
                  {programacion(d.fechaProgramada, d.horaProgramada)}
                  {/* F12.3: del parte a su día en el Planner (y vuelta), con su centro. */}
                  {puede('partes:leer') && <> · <Link to={`/mantenimiento/planner?vista=dia&fecha=${d.fechaProgramada}&idCentro=${d.centro.id}`} state={volverAqui}>Ver en el Planner</Link></>}
                </>} />
                {d.duracionEstimadaMin !== null && <Dato e="Duración estimada" v={`${String(d.duracionEstimadaMin)} min`} />}
                {d.planPreventivo !== null && (
                  <Dato e="Origen" v={<>Mantenimiento preventivo: <Link to={`/mantenimiento/preventivos/${d.planPreventivo.id}`}>{d.planPreventivo.nombre}</Link>{d.fechaOcurrencia !== null && <> · previsto el {fecha(d.fechaOcurrencia)}</>}</>} />
                )}
              </div>
            </section>

            <section className="panel" aria-label="Intervenciones">
              <div className="panel-cabecera"><h2>Intervenciones</h2></div>
              <div className="panel-cuerpo">
                {d.intervenciones.length === 0 ? <p className="suave">Aún no hay visitas. Cada visita es una intervención: empieza cuando un técnico asignado pulsa «Iniciar intervención».</p> : (
                  <ol className="lista-intervenciones">
                    {d.intervenciones.map((x, k) => (
                      <li key={x.id}>
                        <strong>Intervención {k + 1}</strong> · {x.operario.nombre} · {fechaHora(x.inicioEn)}{x.finEn === null ? ' · en curso' : ` → ${fechaHora(x.finEn)}`}
                        {x.resultado !== null && <span className={`etiqueta ${x.resultado === 'RESUELTO' ? 'exito' : 'aviso'}`}>{RESULTADOS[x.resultado]}</span>}
                        {x.diagnostico !== null && <div className="pequena">Diagnóstico: {x.diagnostico}</div>}
                        {x.trabajoRealizado !== null && <div className="pequena">Trabajo: {x.trabajoRealizado}</div>}
                        {/* FASE 9.5-D: ejecutor ≠ quien la registró; sólo se dice cuando difieren. */}
                        {x.registradaPorEjecutor === false && x.registradaPor !== undefined && (
                          <div className="suave pequena">Registrada por {x.registradaPor.nombre} en nombre de {x.operario.nombre}</div>
                        )}
                        {x.finalizadaPor != null && x.registradaPor !== undefined && x.finalizadaPor.id !== x.registradaPor.id && (
                          <div className="suave pequena">Finalizada por {x.finalizadaPor.nombre}</div>
                        )}
                        {/* FASE 9.5-B (hallazgo UAT): quien gestiona puede terminar la intervención abierta de un técnico. */}
                        {gestiona && x.finEn === null && x.id !== d.miIntervencionAbierta && (
                          <div><button type="button" className="pequeno" onClick={() => { setIntervencion(x.id); setDialogo('finalizar-intervencion'); }}>
                            Finalizar esta intervención
                          </button></div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </section>

            {d.incidencias.length > 0 && (
              <section className="panel" aria-label="Incidencias vinculadas">
                <div className="panel-cabecera"><h2>Incidencias</h2></div>
                <ul className="panel-cuerpo">
                  {d.incidencias.map((i) => <li key={i.id}><Link to={`/mantenimiento/incidencias/${i.id}`} state={volverAqui}>{i.titulo}</Link> <span className="suave pequena">{i.codigo}</span></li>)}
                </ul>
              </section>
            )}

            <EvidenciasMnt
              rutaLista={`${base}/evidencias`}
              rutaSubida={`${base}/evidencias`}
              puedeSubir={vivo && (gestiona || d.soyTecnicoAsignado)}
              puedeAnular={gestiona}
              puedePublicar={gestiona && d.visibleCliente}
              motivoNoPublicable={!gestiona ? 'Uso interno: sólo quien gestiona puede publicar evidencias al cliente.'
                : 'Uso interno: este parte no es visible para el cliente (sólo lo son los de su incidencia o los preventivos).'}
              intervenciones={d.intervenciones.map((x, k) => ({ id: x.id, etiqueta: `Intervención ${k + 1} (${x.operario.nombre})` }))}
            />

            {puede('consumos_pt:leer') && (
              //  key: al cambiar el estado del parte (cerrar, finalizar…) los materiales se recargan con sus capacidades nuevas.
              <MaterialesParte key={d.estado} idPt={d.id} alCambiar={() => parte.recargar()}
                intervenciones={d.intervenciones.map((x, k) => ({ id: x.id, etiqueta: `Intervención ${String(k + 1)} · ${x.operario.nombre} · ${fechaHora(x.inicioEn)}`, finalizada: x.finEn !== null }))} />
            )}

            <SeccionTecnicos parte={d} gestiona={gestiona} accion={accion} />
            {gestiona && <SeccionActivos parte={d} gestiona={gestiona} accion={accion} />}

            <section className="panel" aria-label="Información del parte">
              <div className="panel-cabecera"><h2>Información del parte</h2></div>
              <div className="panel-cuerpo datos">
                <Dato e="Cliente" v={d.cliente.nombre} />
                {d.direccionCentro !== null && d.direccionCentro !== '' && <Dato e="Dirección del centro" v={d.direccionCentro} />}
                {d.fechaObjetivo !== null && <Dato e="Fecha objetivo" v={fecha(d.fechaObjetivo)} />}
                <Dato e="Alta" v={`${d.creadoPor.nombre} · ${fechaHora(d.creadoEn)}`} />
                <Dato e="Área cliente" v={d.visibleCliente ? <span className="etiqueta info">Visible para el cliente</span> : 'Interno: el cliente no lo ve'} />
              </div>
              {d.visibleCliente && (
                <div className="panel-cuerpo">
                  <div className="etiqueta-dato">Resumen para el cliente</div>
                  <p className="pequena" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{d.resumenPublico ?? 'Sin resumen todavía: el cliente sólo ve el título, el estado y las fechas.'}</p>
                </div>
              )}
            </section>
          </>
        )}
      </Panel>

      <section className="panel" aria-label="Historial">
        <div className="panel-cabecera"><h2>Historial</h2></div>
        <div className="panel-cuerpo">
          <Panel estado={historial}>{(h) => <HistorialPt eventos={h.filas} />}</Panel>
          <PieHistorial historial={historial} />
        </div>
      </section>

      {p !== null && dialogo === 'editar' && <DialogoEditar parte={p} cerrar={() => setDialogo(null)} accion={accion} />}
      {p !== null && dialogo === 'planificar' && <DialogoPlanificar parte={p} cerrar={() => setDialogo(null)} accion={accion} />}
      {p !== null && dialogo === 'pendiente' && (
        <DialogoPendiente titulo="Poner pendiente" cerrar={() => setDialogo(null)}
          enviar={(m, det) => accion(() => api.post(`${base}/estado`, { estado: 'PENDIENTE', motivoPendiente: m, detallePendiente: det }), 'Parte puesto en pendiente.')} />
      )}
      {p !== null && dialogo === 'finalizar-parte' && (
        <DialogoTexto titulo="Finalizar el parte"
          explicacion="El trabajo técnico queda terminado. Después se cierra, cuando todo esté resuelto (material validado o devuelto)." etiqueta="" obligatorio={false}
          boton="Finalizar parte" cerrar={() => setDialogo(null)} enviar={() => accion(() => api.post(`${base}/estado`, { estado: 'FINALIZADO' }), 'Parte finalizado.')} sinTexto />
      )}
      {p !== null && dialogo === 'cerrar' && (
        <DialogoCerrar parte={p} cerrar={() => setDialogo(null)}
          enviar={(obs, resumen) => accion(() => api.post(`${base}/estado`, { estado: 'CERRADO', observacionesCierre: obs || null, ...(resumen !== '' ? { resumenPublico: resumen } : {}) }), 'Parte cerrado correctamente.')} />
      )}
      {p !== null && dialogo === 'nueva-intervencion' && (
        <DialogoNuevaIntervencion parte={p} cerrar={() => setDialogo(null)}
          enviar={(idOperario, diagnostico) => accion(() => api.post(`${base}/intervenciones`, { idOperario, ...(diagnostico !== '' ? { diagnostico } : {}) }), 'Intervención creada.')} />
      )}
      {p !== null && dialogo === 'reabrir' && (
        <DialogoReabrir parte={p} cerrar={() => setDialogo(null)}
          enviar={(motivo, detalle) => accion(() => api.post(`${base}/estado`, { estado: 'REABIERTO', motivoReapertura: motivo, detalleReapertura: detalle }), 'Parte reabierto.')} />
      )}
      {p !== null && dialogo === 'resolver-reapertura' && (
        <DialogoTexto titulo="Finalizar sin nueva intervención"
          explicacion="La reapertura se resuelve sin volver a intervenir (por ejemplo, una corrección de material). El parte queda finalizado; después se cierra como siempre."
          etiqueta="Cómo se ha resuelto *" obligatorio boton="Finalizar parte" cerrar={() => setDialogo(null)}
          enviar={(t) => accion(() => api.post(`${base}/estado`, { estado: 'FINALIZADO', resolucionReapertura: t }), 'Parte finalizado sin nueva intervención.')} />
      )}
      {p !== null && dialogo === 'anular' && (
        <DialogoTexto titulo={`Anular ${p.codigo}`} explicacion="El parte no procede. Es definitivo y queda en el historial." etiqueta="Motivo" obligatorio
          boton="Anular" peligro cerrar={() => setDialogo(null)} enviar={(t) => accion(() => api.post(`${base}/estado`, { estado: 'ANULADO', motivoAnulacion: t }), 'Parte anulado.')} />
      )}
      {p !== null && dialogo === 'finalizar-intervencion' && intervencion !== null && (
        <DialogoFinalizarIntervencion parte={p} idIntervencion={intervencion} cerrar={() => setDialogo(null)}
          enviar={(cuerpo) => accion(() => api.post(`${base}/intervenciones/${intervencion}/finalizar`, cuerpo),
            cuerpo['decision'] === 'FINALIZAR_Y_CERRAR' ? 'Parte finalizado y cerrado.' : 'Intervención finalizada.')} />
      )}
     </div>
    </Shell>
  );
}

function SeccionTecnicos({ parte: p, gestiona, accion }: { parte: ParteDetalle; gestiona: boolean; accion: (f: () => Promise<unknown>, exito?: string) => Promise<void> }): ReactNode {
  const editable = gestiona && !['FINALIZADO', 'CERRADO', 'ANULADO'].includes(p.estado);
  const elegibles = useDatos(
    () => (editable ? api.get<{ filas: TecnicoElegible[] }>(`/api/mantenimiento/tecnicos?idCentro=${p.centro.id}`) : Promise.resolve({ filas: [] as TecnicoElegible[] })),
    [editable, p.centro.id],
  );
  const [elegido, setElegido] = useState('');
  const libres = (elegibles.datos?.filas ?? []).filter((t) => !p.tecnicosDetalle.some((x) => x.idOperario === t.idOperario));
  return (
    <section className="panel" aria-label="Técnicos">
      <div className="panel-cabecera"><h2>Técnicos</h2></div>
      <div className="panel-cuerpo">
        {p.tecnicosDetalle.length === 0 ? <p className="suave">Sin técnicos asignados.</p> : (
          <ul>
            {p.tecnicosDetalle.map((t) => (
              <li key={t.idOperario} className="fila">
                {t.nombre}
                {editable && <button className="pequeno" onClick={() => void accion(() => api.post(`/api/mantenimiento/partes/${p.id}/tecnicos/${t.idOperario}/desasignar`), 'Técnico retirado del parte.')}>Quitar</button>}
              </li>
            ))}
          </ul>
        )}
        {editable && (
          <div className="fila" style={{ flexWrap: 'wrap' }}>
            <label className="campo"><span>Añadir técnico</span>
              <select value={elegido} onChange={(ev) => setElegido(ev.target.value)}>
                <option value="">Elige…</option>
                {libres.map((t) => <option key={t.idOperario} value={t.idOperario}>{t.nombre}{t.especialidadOperativa === 'AMBAS' ? ' (limpieza y mantenimiento)' : ''}</option>)}
              </select>
            </label>
            <button disabled={elegido === ''} onClick={() => { void accion(() => api.post(`/api/mantenimiento/partes/${p.id}/tecnicos`, { idOperario: elegido }), 'Técnico asignado.'); setElegido(''); }}>Asignar</button>
          </div>
        )}
      </div>
    </section>
  );
}

function SeccionActivos({ parte: p, gestiona, accion }: { parte: ParteDetalle; gestiona: boolean; accion: (f: () => Promise<unknown>, exito?: string) => Promise<void> }): ReactNode {
  const editable = gestiona && !['FINALIZADO', 'CERRADO', 'ANULADO'].includes(p.estado);
  const disponibles = useDatos(
    () => (editable ? api.get<{ filas: ActivoResumen[]; total: number }>(`/api/activos?idCentro=${p.centro.id}&porPagina=200`) : Promise.resolve({ filas: [] as ActivoResumen[], total: 0 })),
    [editable, p.centro.id],
  );
  const [elegido, setElegido] = useState('');
  const libres = (disponibles.datos?.filas ?? []).filter((a) => !p.activosDetalle.some((x) => x.id === a.id));
  return (
    <section className="panel" aria-label="Activos">
      <div className="panel-cabecera"><h2>Activos</h2></div>
      <div className="panel-cuerpo">
        {p.activosDetalle.length === 0 ? <p className="suave">Parte sin activo concreto.</p> : (
          <ul>
            {p.activosDetalle.map((a) => (
              <li key={a.id} className="fila" style={{ flexWrap: 'wrap' }}>
                <Link to={`/activos/${a.id}`}>{a.codigo} — {a.nombre}</Link>
                <span className="suave pequena">{a.tipo}{a.ubicacion !== null ? ` · ${a.ubicacion}` : ''}</span>
                {a.estado !== 'OPERATIVO' && <span className={`etiqueta ${a.estado === 'BAJA' ? 'oscura' : 'aviso'}`}>{a.estado === 'BAJA' ? 'Baja' : 'Fuera de servicio'}</span>}
                {editable && <button className="pequeno" onClick={() => void accion(() => api.post(`/api/mantenimiento/partes/${p.id}/activos/${a.id}/retirar`), 'Activo retirado del parte.')}>Quitar</button>}
              </li>
            ))}
          </ul>
        )}
        <p className="suave pequena">El estado del activo no cambia con el parte: si hace falta, márcalo fuera de servicio desde su ficha.</p>
        {editable && (
          <div className="fila" style={{ flexWrap: 'wrap' }}>
            <label className="campo"><span>Añadir activo</span>
              <select value={elegido} onChange={(ev) => setElegido(ev.target.value)}>
                <option value="">Elige…</option>
                {libres.map((a) => <option key={a.id} value={a.id}>{a.nombre}{a.titularidad === 'EMPRESA' ? ' (empresa prestadora)' : ''} · {a.codigo}</option>)}
              </select>
            </label>
            <button disabled={elegido === ''} onClick={() => { void accion(() => api.post(`/api/mantenimiento/partes/${p.id}/activos`, { idActivo: elegido }), 'Activo añadido al parte.'); setElegido(''); }}>Añadir</button>
          </div>
        )}
      </div>
    </section>
  );
}

const TITULOS_EVENTO: Readonly<Record<string, string>> = {
  ALTA: 'Alta del parte',
  EDICION: 'Datos editados',
  PROGRAMACION: 'Programación',
  ASIGNACION: 'Técnico asignado',
  DESASIGNACION: 'Técnico retirado',
  ACTIVO_ANADIDO: 'Activo añadido',
  ACTIVO_RETIRADO: 'Activo retirado',
  INTERVENCION_INICIADA: 'Intervención iniciada',
  INTERVENCION_FINALIZADA: 'Intervención finalizada',
  INCIDENCIA_VINCULADA: 'Incidencia vinculada',
};

function HistorialPt({ eventos }: { eventos: readonly EventoPt[] }): ReactNode {
  if (eventos.length === 0) return <p className="suave">Sin eventos.</p>;
  return (
    <ol className="lista-historial-activo" aria-label="Historial del parte" style={{ margin: 0, paddingLeft: 18 }}>
      {eventos.map((e) => (
        <li key={e.id} style={{ marginBottom: 8 }}>
          <strong>{e.tipo !== 'ESTADO' ? TITULOS_EVENTO[e.tipo] ?? e.tipo
            : e.estadoNuevo === 'REABIERTO' ? 'Parte reabierto' : e.estadoNuevo === 'CERRADO' ? 'Parte cerrado'
            : `Estado: ${estadoPtMantenimiento(e.estadoAnterior).label} → ${estadoPtMantenimiento(e.estadoNuevo).label}`}</strong>
          {e.operario !== null && <span> · {e.actorEsEjecutor === false ? `ejecutor: ${e.operario}` : e.operario}</span>}
          {e.actorEsEjecutor === false && <div className="pequena">Registrada por {e.usuario.nombre} (no es el técnico ejecutor)</div>}
          {e.activo !== null && <span> · {e.activo}</span>}
          {e.incidencia !== null && <span> · {e.incidencia}</span>}
          {e.estadoNuevo === 'REABIERTO' && typeof e.cambios?.['motivoReapertura'] === 'string' && (
            <div className="pequena">Motivo: {MOTIVOS_REAPERTURA[e.cambios['motivoReapertura']] ?? e.cambios['motivoReapertura']}</div>
          )}
          {e.motivo !== null && <div className="pequena">{RESULTADOS[e.motivo] ?? MOTIVOS_PENDIENTE[e.motivo] ?? e.motivo}</div>}
          {e.estadoNuevo === 'REABIERTO' && <CierreAnterior c={e.cambios?.['cierreAnterior']} />}
          <div className="suave pequena">{e.usuario.nombre} · {fechaHora(e.ocurridoEn)}</div>
        </li>
      ))}
    </ol>
  );
}

/** FASE 9.5-C — el cierre que se reabrió, tal y como lo guardó el historial. */
function CierreAnterior({ c }: { c: unknown }): ReactNode {
  if (c === null || typeof c !== 'object') return null;
  const x = c as { cerradoEn?: string | null; cerradoPorNombre?: string; observacionesCierre?: string | null };
  return (
    <div className="suave pequena">
      Cierre anterior: {x.cerradoEn != null ? fechaHora(x.cerradoEn) : '—'}{x.cerradoPorNombre !== undefined && ` por ${x.cerradoPorNombre}`}
      {x.observacionesCierre != null && ` · «${x.observacionesCierre}»`}
    </div>
  );
}

// ---------------------------------------------------------------- diálogos

/**
 * FASE 9.5-D — quien gestiona inicia una intervención atribuida al TÉCNICO EJECUTOR real (asignado al parte).
 * Nunca a sí mismo por ser quien la registra: el sistema guarda los dos (ejecutor y quién la registró).
 */
function DialogoNuevaIntervencion({ parte: p, cerrar, enviar }: { parte: ParteDetalle; cerrar: () => void; enviar: (idOperario: string, diagnostico: string) => Promise<void> }): ReactNode {
  const ocupados = new Set(p.intervenciones.filter((x) => x.finEn === null).map((x) => x.operario.id));
  const libres = p.tecnicosDetalle.filter((t) => !ocupados.has(t.idOperario));
  const [idOperario, setIdOperario] = useState(libres.length === 1 ? libres[0]!.idOperario : '');
  const [diagnostico, setDiagnostico] = useState('');
  return (
    <Modal titulo={`Nueva intervención · ${p.codigo}`} cerrar={cerrar}>
      <form aria-label="Nueva intervención" onSubmit={(ev) => { ev.preventDefault(); if (idOperario !== '') void enviar(idOperario, diagnostico.trim()); }}>
        <p className="suave">La intervención queda a nombre del técnico que la ejecuta; tú constas como quien la registra.</p>
        {libres.length === 0 ? (
          <p className="nota aviso">No hay técnicos asignados disponibles. Asigna primero el técnico en la sección «Técnicos».</p>
        ) : (
          <div className="campo"><label htmlFor="ni-tecnico">Técnico ejecutor *</label>
            <select id="ni-tecnico" required value={idOperario} onChange={(ev) => setIdOperario(ev.target.value)}>
              <option value="">Elige…</option>
              {libres.map((t) => <option key={t.idOperario} value={t.idOperario}>{t.nombre}</option>)}
            </select></div>
        )}
        <div className="campo"><label htmlFor="ni-diag">Diagnóstico inicial (opcional)</label>
          <textarea id="ni-diag" rows={2} maxLength={4000} value={diagnostico} onChange={(ev) => setDiagnostico(ev.target.value)} /></div>
        <div className="fila"><button type="submit" className="principal" disabled={idOperario === ''}>Iniciar intervención</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

/** FASE 9.5-C — reabrir un PT cerrado: tipo de motivo + explicación (obligatorios). */
function DialogoReabrir({ parte: p, cerrar, enviar }: { parte: ParteDetalle; cerrar: () => void; enviar: (motivo: string, detalle: string) => Promise<void> }): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState('');
  const valido = motivo !== '' && detalle.trim().length >= 5;
  return (
    <Modal titulo={`Reabrir ${p.codigo}`} cerrar={cerrar}>
      <form aria-label={`Reabrir ${p.codigo}`} onSubmit={(ev) => { ev.preventDefault(); if (valido) void enviar(motivo, detalle.trim()); }}>
        <p className="nota aviso">El cierre anterior queda en el historial. El parte vuelve a estar abierto: podrás retomar el trabajo con una nueva intervención.</p>
        <div className="campo"><label htmlFor="reap-motivo">Motivo de reapertura *</label>
          <select id="reap-motivo" required value={motivo} onChange={(ev) => setMotivo(ev.target.value)}>
            <option value="">Elige…</option>
            {Object.entries(MOTIVOS_REAPERTURA).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select></div>
        <div className="campo"><label htmlFor="reap-detalle">Explicación *</label>
          <textarea id="reap-detalle" rows={3} required maxLength={2000} value={detalle} onChange={(ev) => setDetalle(ev.target.value)} />
          <p className="suave pequena">Interna: el cliente sólo ve que el parte está «Reabierto».</p></div>
        <div className="fila"><button type="submit" className="principal" disabled={!valido}>Reabrir parte</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

function DialogoEditar({ parte: p, cerrar, accion }: { parte: ParteDetalle; cerrar: () => void; accion: (f: () => Promise<unknown>, exito?: string) => Promise<void> }): ReactNode {
  const [v, setV] = useState({
    titulo: p.titulo, descripcion: p.descripcion ?? '', tipo: p.tipo, prioridad: p.prioridad,
    fechaProgramada: p.fechaProgramada ?? '', horaProgramada: p.horaProgramada ?? '',
    duracion: p.duracionEstimadaMin === null ? '' : String(p.duracionEstimadaMin), fechaObjetivo: p.fechaObjetivo ?? '',
    resumenPublico: p.resumenPublico ?? '',
  });
  const poner = (k: keyof typeof v) => (x: string) => setV((a) => ({ ...a, [k]: x }));
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    const cambios: Record<string, unknown> = { versionEsperada: p.actualizadoEn };
    if (v.titulo !== p.titulo) cambios['titulo'] = v.titulo;
    if (v.descripcion !== (p.descripcion ?? '')) cambios['descripcion'] = v.descripcion || null;
    if (v.tipo !== p.tipo) cambios['tipo'] = v.tipo;
    if (v.prioridad !== p.prioridad) cambios['prioridad'] = v.prioridad;
    if (v.fechaProgramada !== (p.fechaProgramada ?? '') || v.horaProgramada !== (p.horaProgramada ?? '')) {
      cambios['fechaProgramada'] = v.fechaProgramada || null;
      cambios['horaProgramada'] = v.horaProgramada || null;
    }
    if (v.duracion !== (p.duracionEstimadaMin === null ? '' : String(p.duracionEstimadaMin))) cambios['duracionEstimadaMin'] = v.duracion === '' ? null : Number(v.duracion);
    if (v.fechaObjetivo !== (p.fechaObjetivo ?? '')) cambios['fechaObjetivo'] = v.fechaObjetivo || null;
    if (v.resumenPublico !== (p.resumenPublico ?? '')) cambios['resumenPublico'] = v.resumenPublico.trim() || null;
    await accion(() => api.patch(`/api/mantenimiento/partes/${p.id}`, cambios), 'Cambios guardados.');
  }
  return (
    <Modal titulo={`Editar ${p.codigo}`} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Editar parte">
        <div className="campo"><label htmlFor="ed-pt-titulo">Título</label><input id="ed-pt-titulo" value={v.titulo} onChange={(ev) => poner('titulo')(ev.target.value)} /></div>
        <div className="campo"><label htmlFor="ed-pt-desc">Descripción</label><textarea id="ed-pt-desc" rows={3} value={v.descripcion} onChange={(ev) => poner('descripcion')(ev.target.value)} /></div>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="ed-pt-tipo">Tipo</label>
            <select id="ed-pt-tipo" value={v.tipo} onChange={(ev) => poner('tipo')(ev.target.value)}>{Object.entries(TIPOS_PT).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select></div>
          <div className="campo"><label htmlFor="ed-pt-prio">Prioridad</label>
            <select id="ed-pt-prio" value={v.prioridad} onChange={(ev) => poner('prioridad')(ev.target.value)}>{['BAJA', 'MEDIA', 'ALTA', 'CRITICA'].map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
        </div>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="ed-pt-fecha">Fecha programada</label><input id="ed-pt-fecha" type="date" value={v.fechaProgramada} onChange={(ev) => poner('fechaProgramada')(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="ed-pt-hora">Hora</label><input id="ed-pt-hora" type="time" value={v.horaProgramada} onChange={(ev) => poner('horaProgramada')(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="ed-pt-dur">Duración (min)</label><input id="ed-pt-dur" type="number" min={1} value={v.duracion} onChange={(ev) => poner('duracion')(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="ed-pt-obj">Fecha objetivo</label><input id="ed-pt-obj" type="date" value={v.fechaObjetivo} onChange={(ev) => poner('fechaObjetivo')(ev.target.value)} /></div>
        </div>
        {p.visibleCliente && (
          <div className="campo">
            <label htmlFor="ed-pt-resumen">Resumen para el cliente (opcional)</label>
            <textarea id="ed-pt-resumen" rows={2} value={v.resumenPublico} onChange={(ev) => poner('resumenPublico')(ev.target.value)} />
            <p className="suave pequena">Este parte es visible en el área cliente. El cliente ve el título y este resumen; nunca la descripción, el diagnóstico ni los técnicos.</p>
          </div>
        )}
        <div className="fila"><button type="submit" className="principal">Guardar</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

/** F12.1 — lo que se explica antes de cerrar (también en «Finalizar y cerrar»). */
const TEXTO_CERRAR = 'Cerrar termina el parte. Podrá reabrirse posteriormente si fuera necesario.';

/**
 * Cierre: todo resuelto. Si el parte es visible para el cliente, el resumen para él (F12.1: también lo escribe el
 * técnico; si cierra el técnico y aún no hay resumen, es obligatorio — lo dice el servidor).
 */
function CampoResumenCliente({ obligatorio, valor, cambiar, id }: { obligatorio: boolean; valor: string; cambiar: (v: string) => void; id: string }): ReactNode {
  return (
    <div className="campo">
      <label htmlFor={id}>Resumen para el cliente{obligatorio ? ' *' : ' (opcional)'}</label>
      <textarea id={id} rows={2} value={valor} required={obligatorio} onChange={(ev) => cambiar(ev.target.value)} />
      <p className="suave pequena">El cliente ve este parte: cuéntale qué se ha hecho. Las observaciones de cierre son internas; el cliente sólo ve este resumen.</p>
    </div>
  );
}

function DialogoCerrar({ parte: p, cerrar, enviar }: { parte: ParteDetalle; cerrar: () => void; enviar: (obs: string, resumen: string) => Promise<void> }): ReactNode {
  const [obs, setObs] = useState('');
  const [resumen, setResumen] = useState(p.resumenPublico ?? '');
  const obligatorio = p.resumenObligatorioAlCerrar === true;
  const valido = !obligatorio || resumen.trim().length >= 5;
  return (
    <Modal titulo={`Cerrar ${p.codigo}`} cerrar={cerrar}>
      <form aria-label={`Cerrar ${p.codigo}`} onSubmit={(ev) => { ev.preventDefault(); if (valido) void enviar(obs.trim(), resumen.trim() === (p.resumenPublico ?? '') ? '' : resumen.trim()); }}>
        <p>{TEXTO_CERRAR}</p>
        {p.visibleCliente && <CampoResumenCliente id="cierre-resumen" obligatorio={obligatorio} valor={resumen} cambiar={setResumen} />}
        <div className="campo">
          <label htmlFor="cierre-obs">Observaciones de cierre (opcional, internas)</label>
          <textarea id="cierre-obs" rows={3} value={obs} onChange={(ev) => setObs(ev.target.value)} />
        </div>
        <div className="fila"><button type="submit" className="principal" disabled={!valido}>Cerrar parte</button><button type="button" onClick={cerrar}>Volver</button></div>
      </form>
    </Modal>
  );
}

function DialogoPlanificar({ parte: p, cerrar, accion }: { parte: ParteDetalle; cerrar: () => void; accion: (f: () => Promise<unknown>, exito?: string) => Promise<void> }): ReactNode {
  const [fechaP, setFechaP] = useState(p.fechaProgramada ?? '');
  const [horaP, setHoraP] = useState(p.horaProgramada ?? '');
  //  F12.3: sin duración no se puede planificar (ni saber si choca): se pide aquí mismo, sin ir a «Editar».
  const [duracion, setDuracion] = useState(p.duracionEstimadaMin === null ? '' : String(p.duracionEstimadaMin));
  const durOk = /^\d+$/.test(duracion) && Number(duracion) >= 1 && Number(duracion) <= 10080;
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    await accion(async () => {
      let avisos: AvisoProgramacion[] = [];
      const cambiaDur = String(p.duracionEstimadaMin ?? '') !== duracion;
      if (fechaP !== (p.fechaProgramada ?? '') || horaP !== (p.horaProgramada ?? '') || cambiaDur) {
        avisos = (await api.patch<{ avisos: AvisoProgramacion[] }>(`/api/mantenimiento/partes/${p.id}`, {
          fechaProgramada: fechaP, horaProgramada: horaP, ...(cambiaDur ? { duracionEstimadaMin: Number(duracion) } : {}),
        })).avisos;
      }
      await api.post(`/api/mantenimiento/partes/${p.id}/estado`, { estado: 'PLANIFICADO' });
      return { avisos };
    }, 'Parte planificado.');
  }
  return (
    <Modal titulo={`Planificar ${p.codigo}`} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Planificar parte">
        <p>Hace falta fecha, hora, cuánto durará y al menos un técnico asignado{p.tecnicosDetalle.length === 0 ? ' — asígnalo antes en «Técnicos».' : '.'}</p>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="pl-fecha">Fecha</label><input id="pl-fecha" type="date" required value={fechaP} onChange={(ev) => setFechaP(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="pl-hora">Hora</label><input id="pl-hora" type="time" required value={horaP} onChange={(ev) => setHoraP(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="pl-duracion">Duración (minutos)</label>
            <input id="pl-duracion" type="number" min={1} max={10080} required inputMode="numeric" value={duracion} onChange={(ev) => setDuracion(ev.target.value)} /></div>
        </div>
        {!durOk && duracion !== '' && <div className="error-campo">Duración en minutos, entre 1 y 10080.</div>}
        <div className="fila"><button type="submit" className="principal" disabled={p.tecnicosDetalle.length === 0 || !durOk}>Planificar</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

function SelectorMotivo({ motivo, setMotivo, detalle, setDetalle }: { motivo: string; setMotivo: (v: string) => void; detalle: string; setDetalle: (v: string) => void }): ReactNode {
  return (
    <>
      <div className="campo">
        <label htmlFor="motivo-pendiente">Motivo</label>
        <select id="motivo-pendiente" value={motivo} required onChange={(ev) => setMotivo(ev.target.value)}>
          <option value="">Elige…</option>
          {Object.entries(MOTIVOS_PENDIENTE).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
      </div>
      <div className="campo">
        <label htmlFor="detalle-pendiente">{motivo === 'OTRO' ? 'Explica el motivo' : 'Detalle (opcional)'}</label>
        <input id="detalle-pendiente" value={detalle} required={motivo === 'OTRO'} onChange={(ev) => setDetalle(ev.target.value)} />
      </div>
    </>
  );
}

function DialogoPendiente({ titulo, cerrar, enviar }: { titulo: string; cerrar: () => void; enviar: (m: string, d: string | null) => Promise<void> }): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState('');
  return (
    <Modal titulo={titulo} cerrar={cerrar}>
      <form onSubmit={(ev) => { ev.preventDefault(); void enviar(motivo, detalle.trim() || null); }} aria-label={titulo}>
        <SelectorMotivo motivo={motivo} setMotivo={setMotivo} detalle={detalle} setDetalle={setDetalle} />
        <div className="fila"><button type="submit" className="principal" disabled={motivo === '' || (motivo === 'OTRO' && detalle.trim().length < 5)}>Poner pendiente</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

function DialogoTexto({ titulo, explicacion, etiqueta, obligatorio, boton, cerrar, enviar, sinTexto = false, peligro = false }: {
  titulo: string; explicacion: string; etiqueta: string; obligatorio: boolean; boton: string; cerrar: () => void; enviar: (t: string) => Promise<void>; sinTexto?: boolean;
  /** UX-1B: acción destructiva: el botón final en rojo sólido (sólo aquí). */
  peligro?: boolean;
}): ReactNode {
  const [t, setT] = useState('');
  return (
    <Modal titulo={titulo} cerrar={cerrar}>
      <form onSubmit={(ev) => { ev.preventDefault(); void enviar(t.trim()); }} aria-label={titulo}>
        <p>{explicacion}</p>
        {!sinTexto && (
          <div className="campo"><label htmlFor="dialogo-texto">{etiqueta}</label><textarea id="dialogo-texto" rows={3} value={t} required={obligatorio} onChange={(ev) => setT(ev.target.value)} /></div>
        )}
        <div className="fila"><button type="submit" className={peligro ? 'peligro' : 'principal'} disabled={obligatorio && t.trim().length < 5}>{boton}</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

function DialogoFinalizarIntervencion({ parte: p, idIntervencion, cerrar, enviar }: { parte: ParteDetalle; idIntervencion: string; cerrar: () => void; enviar: (c: Record<string, unknown>) => Promise<void> }): ReactNode {
  const actual = p.intervenciones.find((x) => x.id === idIntervencion);
  const [diagnostico, setDiagnostico] = useState(actual?.diagnostico ?? '');
  const [trabajo, setTrabajo] = useState('');
  const [resultado, setResultado] = useState('');
  const [decision, setDecision] = useState('CONTINUAR');
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState('');
  const [resumen, setResumen] = useState(p.resumenPublico ?? '');
  const otrasAbiertas = p.intervenciones.filter((x) => x.finEn === null && x.id !== idIntervencion).length;
  const material = p.materialPendiente ?? { consumos: 0, devoluciones: 0 };
  const conMaterial = material.consumos + material.devoluciones > 0;
  //  Sólo se ofrece lo que tiene sentido: finalizar (y cerrar) el parte, si está resuelto y nadie más sigue trabajando.
  const opciones = useMemo(() => {
    const o: [string, string][] = [['CONTINUAR', 'Seguir con el parte (otra visita más tarde)']];
    if (otrasAbiertas === 0) {
      o.push(['PENDIENTE', 'Dejar el parte pendiente']);
      if (resultado === 'RESUELTO') {
        o.push(['FINALIZAR', 'Finalizar el parte (se cierra después)']);
        if (!conMaterial) o.push(['FINALIZAR_Y_CERRAR', 'Finalizar y cerrar el parte']);
      }
    }
    return o;
  }, [otrasAbiertas, resultado, conMaterial]);
  useEffect(() => {
    if (!opciones.some(([k]) => k === decision)) setDecision('CONTINUAR');
  }, [opciones, decision]);
  const resumenObligatorio = decision === 'FINALIZAR_Y_CERRAR' && p.resumenObligatorioAlCerrar === true;
  const valido = trabajo.trim().length >= 5 && resultado !== ''
    && (decision !== 'PENDIENTE' || (motivo !== '' && (motivo !== 'OTRO' || detalle.trim().length >= 5)))
    && (!resumenObligatorio || resumen.trim().length >= 5);
  return (
    <Modal titulo="Finalizar intervención" cerrar={cerrar}>
      <form aria-label="Finalizar intervención" onSubmit={(ev) => {
        ev.preventDefault();
        void enviar({
          diagnostico: diagnostico.trim() || null, trabajoRealizado: trabajo, resultado, decision,
          ...(decision === 'PENDIENTE' ? { motivoPendiente: motivo, detallePendiente: detalle.trim() || null } : {}),
          ...(decision === 'FINALIZAR_Y_CERRAR' && p.visibleCliente && resumen.trim() !== '' && resumen.trim() !== (p.resumenPublico ?? '')
            ? { resumenPublico: resumen.trim() } : {}),
        });
      }}>
        <div className="campo"><label htmlFor="fi-diag">Diagnóstico (qué has encontrado)</label><textarea id="fi-diag" rows={2} value={diagnostico} onChange={(ev) => setDiagnostico(ev.target.value)} /></div>
        <div className="campo"><label htmlFor="fi-trabajo">Trabajo realizado</label><textarea id="fi-trabajo" rows={3} required value={trabajo} onChange={(ev) => setTrabajo(ev.target.value)} /></div>
        <fieldset className="campo">
          <legend>Resultado</legend>
          {Object.entries(RESULTADOS).map(([k, t]) => (
            <label key={k} className="fila opcion-grande"><input type="radio" name="resultado" value={k} checked={resultado === k} onChange={() => setResultado(k)} /> {t}</label>
          ))}
        </fieldset>
        <fieldset className="campo">
          <legend>Siguiente paso</legend>
          {opciones.map(([k, t]) => (
            <label key={k} className="fila opcion-grande"><input type="radio" name="decision" value={k} checked={decision === k} onChange={() => setDecision(k)} /> {t}</label>
          ))}
          {otrasAbiertas > 0 && <p className="suave pequena">Otro técnico sigue trabajando en este parte: el parte cambiará de estado cuando terminen todos.</p>}
          {otrasAbiertas === 0 && resultado !== '' && resultado !== 'RESUELTO' && (
            <p className="suave pequena">Para finalizar el parte, la intervención tiene que quedar como «Resuelto».</p>
          )}
          {otrasAbiertas === 0 && resultado === 'RESUELTO' && conMaterial && (
            <p className="suave pequena">Para cerrarlo hay que resolver antes el material: hay {textoPendientes(material)}.</p>
          )}
        </fieldset>
        {decision === 'PENDIENTE' && <SelectorMotivo motivo={motivo} setMotivo={setMotivo} detalle={detalle} setDetalle={setDetalle} />}
        {decision === 'FINALIZAR_Y_CERRAR' && (
          <>
            <p className="nota info">{TEXTO_CERRAR}</p>
            {p.visibleCliente && <CampoResumenCliente id="fi-resumen" obligatorio={resumenObligatorio} valor={resumen} cambiar={setResumen} />}
          </>
        )}
        <div className="fila"><button type="submit" className="principal grande" disabled={!valido}>Finalizar intervención</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}
